// Pure notification-centre logic: labels, priority styling, deep links, inline actions and live
// cache merging. No React, no fetch; tested in logic.test.ts.
import type { Tone } from '../../design';
import type { NotificationCenter, NotificationItem, Priority } from './contract';

export type CentrePage = NotificationCenter;
export interface CentreData {
  pages: CentrePage[];
  pageParams: unknown[];
}

/** "Ada", "Ada and Grace", "Ada and 4 others". */
export function actorsSummary(actors: NotificationItem['actors'], count: number): string {
  const first = actors[0];
  if (!first) return '';
  const total = Math.max(count, actors.length);
  if (total === 1) return first.name;
  if (total === 2 && actors[1]) return `${first.name} and ${actors[1].name}`;
  const others = total - 1;
  return `${first.name} and ${others} ${others === 1 ? 'other' : 'others'}`;
}

export function priorityTone(p: Priority): { tone: Tone; label: string } | null {
  if (p === 'critical') return { tone: 'stop', label: 'Critical' };
  if (p === 'high') return { tone: 'gate', label: 'High priority' };
  return null;
}

export function viaAgentLabel(v: NotificationItem['viaAgent']): string | null {
  return v ? `via ${v.name} for ${v.ownerName}` : null;
}

/** In-app destination for a row, or null when there is nowhere safe to go. */
export function notificationHref(n: Pick<NotificationItem, 'entity' | 'link'>): string | null {
  if (n.entity?.kind === 'approval') return `/app/inbox?approval=${encodeURIComponent(n.entity.id)}`;
  const l = n.link;
  if (!l || !l.startsWith('/') || l.startsWith('//')) return null;
  if (l === '/app' || l.startsWith('/app/') || l.startsWith('/app?')) return l;
  return `/app${l}`;
}

export type InlineAction = { kind: 'approval'; id: string } | { kind: 'connection'; id: string };

export function inlineActions(n: Pick<NotificationItem, 'type' | 'entity' | 'actedAt'>): InlineAction | null {
  if (n.actedAt !== null || !n.entity) return null;
  if (n.type === 'approval.requested' && n.entity.kind === 'approval') return { kind: 'approval', id: n.entity.id };
  if (n.type === 'connection.request' && n.entity.kind === 'user') return { kind: 'connection', id: n.entity.id };
  return null;
}

/** Applies a live item to the infinite-list cache: new or regrouped rows go to the top, once. */
export function mergeIncoming(
  data: CentreData | undefined,
  item: NotificationItem,
  unreadCount: number | undefined,
): CentreData | undefined {
  if (!data || data.pages.length === 0) return data;
  const pages = data.pages.map((p) => ({ ...p, items: p.items.filter((i) => i.id !== item.id) }));
  const first = pages[0]!;
  pages[0] = {
    ...first,
    items: [item, ...first.items],
    unreadCount: unreadCount ?? first.unreadCount,
  };
  return { ...data, pages };
}

export function shouldToast(n: Pick<NotificationItem, 'priority' | 'readAt'>, ctx: { centreOpen: boolean }): boolean {
  if (ctx.centreOpen || n.readAt !== null) return false;
  return n.priority === 'high' || n.priority === 'critical';
}

export const CHANNEL_LABELS: Record<string, string> = {
  in_app: 'In-app list',
  realtime: 'Live update',
  email: 'Email',
  agent_inbox: 'Agent inbox',
  mcp: 'MCP',
  webhook: 'Webhook',
};

export const DELIVERY_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  sent: 'Delivered',
  failed: 'Failed',
  suppressed: 'Not sent',
};

const REASONS: Record<string, string> = {
  email_not_configured: 'Email is not set up on this server, so no email could be sent. The in-app notification is unaffected.',
  quiet_hours: 'Held during your quiet hours; it is sent when they end.',
  digest: 'Saved for your email digest instead of an immediate email.',
  blocked: 'Not delivered because one of you has blocked the other.',
  muted: 'Not delivered because you muted this person, thread, post, group or agent.',
  pref_off: 'You turned this off in your notification preferences.',
  agent_cap: 'The agent that sent it reached its limit of 20 notifications an hour to you.',
  agent_originated_off: 'You turned off notifications sent by agents.',
  no_email: 'Your account has no email address to send to.',
  high_only_email: 'You get email for high-priority notifications only.',
  daily_email_cap: 'You had already received 10 emails today, so this went to the digest.',
};

/** Plain-language delivery reason; unknown codes are shown as-is rather than hidden. */
export function deliveryReason(code: string | null): string | null {
  if (!code) return null;
  return REASONS[code] ?? `Reason: ${code}`;
}

/** Human names for event types (settings overrides, subscriptions, agent inbox). */
export const TYPE_LABELS: Record<string, string> = {
  'approval.requested': 'Approval requests',
  'approval.decided': 'Approval decisions',
  'approval.expiring': 'Approvals about to expire',
  'guardrail.blocked': 'Guardrail blocks',
  'agent.token_expiring': 'Agent tokens about to expire',
  'agent.activity_summary': 'Daily agent activity summary',
  'agent.rate_limited': 'An agent is being rate-limited',
  'agent.unacknowledged': 'An agent has not picked up an item',
  'connection.request': 'Connection requests',
  'connection.accept': 'Accepted connection requests',
  'invite.accept': 'Accepted invitations',
  'follow.new': 'New followers',
  endorsement: 'Skill endorsements',
  'recommendation.received': 'Recommendations',
  'message.new': 'New messages',
  'post.mention': 'Mentions',
  'comment.new': 'Comments on your posts',
  'post.reaction': 'Reactions to your posts',
  'comment.reaction': 'Reactions to your comments',
  'post.repost': 'Reposts',
  'application.submitted': 'New applicants',
  'application.status_changed': 'Application status changes',
  'screening.completed': 'Screening results',
  'job_alert.matches': 'Job alert matches',
  'job.expiring': 'Jobs about to expire',
  'campaign.budget_exhausted': 'Campaign budget used up',
  'campaign.daily_cap_hit': 'Campaign daily cap reached',
  'campaign.paused': 'Campaign paused',
  'link.conversion_milestone': 'Link conversion milestones',
  'webhook.disabled': 'Webhook disabled',
  'group.join_requested': 'Group join requests',
  'event.reminder': 'Event reminders',
  'verification.completed': 'Verification completed',
  'verification.failed': 'Verification failed',
  'report.created': 'New reports (staff)',
  'report.resolved': 'Your reports resolved',
  'security.new_signin': 'New sign-ins',
  'security.token_created': 'New agent tokens',
  'security.identity_linked': 'Linked sign-in accounts',
  'account.suspended': 'Account suspension',
};

export const typeLabel = (t: string): string => TYPE_LABELS[t] ?? t;
